const normalizeStatusName = (name?: string | null): string =>
  (name ?? '').trim().toLowerCase();

const matchesAny = (normalized: string, tokens: string[]): boolean =>
  tokens.some((token) => normalized.includes(token));

export const isCancelledStatus = (name?: string | null): boolean =>
  matchesAny(normalizeStatusName(name), ['abgebrochen', 'storniert', 'cancel']);

export const isSuccessTerminalStatus = (name?: string | null): boolean =>
  matchesAny(normalizeStatusName(name), ['beendet', 'abgeschlossen', 'fertig', 'done', 'closed']);

/** Audit or project is closed (successfully or cancelled). */
export const isClosedStatus = (name?: string | null): boolean =>
  isCancelledStatus(name) || isSuccessTerminalStatus(name);

/** @deprecated Use isClosedStatus */
export const isInactiveTerminalStatus = isClosedStatus;

export const isProjectSuccessTerminalStatus = (name?: string | null): boolean =>
  matchesAny(normalizeStatusName(name), ['abgerechnet', 'abgeschlossen', 'beendet', 'fertig', 'done', 'closed']);

/** @deprecated Use isProjectSuccessTerminalStatus */
export const isProjectCompletedStatus = isProjectSuccessTerminalStatus;

export const isProjectClosedStatus = (name?: string | null): boolean =>
  isCancelledStatus(name) || isProjectSuccessTerminalStatus(name);

export interface SplitWorkflowStatuses<T extends { name: string }> {
  flowStatuses: T[];
  cancelledStatuses: T[];
  successTerminalStatuses: T[];
}

/** Linear workflow steps excluding parallel cancel terminals. */
export const splitWorkflowStatuses = <T extends { name: string; sort_order: number }>(
  statuses: T[],
): SplitWorkflowStatuses<T> => {
  const sorted = [...statuses].sort((a, b) => a.sort_order - b.sort_order);
  const cancelledStatuses = sorted.filter((status) => isCancelledStatus(status.name));
  const successTerminalStatuses = sorted.filter((status) => isSuccessTerminalStatus(status.name));
  const flowStatuses = sorted.filter((status) => !isCancelledStatus(status.name));
  return { flowStatuses, cancelledStatuses, successTerminalStatuses };
};

export const isProjectActiveStatus = (name?: string | null): boolean => {
  const normalized = normalizeStatusName(name);
  if (!normalized || isCancelledStatus(name) || isProjectCompletedStatus(name)) {
    return false;
  }
  return matchesAny(normalized, ['aktiv', 'lauf', 'offen', 'arbeit', 'planung', 'umsetzung', 'durchfuehrung', 'reporting', 'rechnung']);
};

export const isAuditRunningStatus = (name?: string | null): boolean => {
  const normalized = normalizeStatusName(name);
  if (!normalized || isCancelledStatus(name) || isSuccessTerminalStatus(name)) {
    return false;
  }
  return matchesAny(normalized, ['lauf', 'offen', 'aktiv', 'in bearbeitung', 'in arbeit', 'durchfuehrung', 'reporting', 'preflight', 'ergebnis']);
};

export const isAuditCompletedStatus = (name?: string | null): boolean =>
  isSuccessTerminalStatus(name);
